import { STATUS_LABELS } from '@/lib/template-labels';
import type { PublishGateReason, TemplateDefinition, TemplateStatus } from '@/services/formTemplates';

// Pure helpers for the template admin pages: publish gate copy, import error lines, the definition
// file and the toasts after a publish or a restore. Axios errors are read structurally, so nothing here imports axios.
// Design: docs/specs/2026-10-07-form-template-library-design.md

type PrepareDrops = Extract<PublishGateReason, { code: 'PREPARE_DROPS' }>;

const MAX_FIELD_ERRORS = 5;
const IMPORT_FAILED_TEXT = "Couldn't import the definition";

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", nbsp: ' ' };

/** A label as plain text: element labels can hold rich-text HTML (the forms app renders label.html). */
function plainLabel(label: string): string {
  return label
    .replace(/<\/?[a-zA-Z][^>]*>/g, '')
    .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (_, name: string) => ENTITIES[name])
    .replace(/\s+/g, ' ')
    .trim();
}

function quoted(names: string[]): string {
  return names.map((name) => `"${plainLabel(name)}"`).join(', ');
}

/** What a PREPARE_DROPS reason says an import would lose, in order; [] when it names nothing. */
function dropParts(reason: PrepareDrops): string[] {
  const parts: string[] = [];
  if (reason.elements.length > 0) {
    parts.push(`${plural(reason.elements.length, 'the question', 'the questions')} ${quoted(reason.elements)}`);
  }
  if (reason.endings.length > 0) {
    parts.push(`${plural(reason.endings.length, 'the ending', 'the endings')} ${quoted(reason.endings)}`);
  }
  if (reason.logicRules > 0) {
    parts.push(`${reason.logicRules} logic ${plural(reason.logicRules, 'rule', 'rules')}`);
  }
  if (reason.scoringRules > 0) {
    parts.push(`${reason.scoringRules} scoring ${plural(reason.scoringRules, 'rule', 'rules')}`);
  }
  return parts;
}

/** One line for a reason a version cannot be published yet. */
export function gateReasonText(reason: PublishGateReason): string {
  switch (reason.code) {
    case 'NO_QUESTION':
      return 'Add at least one question.';
    case 'PREPARE_DROPS': {
      const parts = dropParts(reason);
      if (parts.length === 0) return 'An import would lose part of this template.';
      return `An import would lose ${parts.join(', ')}. Change or remove them in the builder.`;
    }
    case 'RICH_TEXT_STORAGE_URL':
      return reason.count === 1
        ? "1 uploaded image inside rich text can't be frozen. Remove it from the text."
        : `${reason.count} uploaded images inside rich text can't be frozen. Remove them from the text.`;
    case 'MISSING_IMAGE':
      return reason.count === 1
        ? '1 image no longer exists in storage. Replace it in the builder.'
        : `${reason.count} images no longer exist in storage. Replace them in the builder.`;
    case 'TOO_MANY_IMAGES':
      return `The template uses more than ${reason.max} images. Remove some in the builder.`;
    default:
      return `Can't be published yet (${(reason as { code: string }).code}).`;
  }
}

function responseOf(error: unknown): { status?: number; data?: any } {
  const response = (error as { response?: { status?: number; data?: unknown } } | null | undefined)?.response;
  return response && typeof response === 'object' ? response : {};
}

/**
 * True when nothing told the person what went wrong: the api client toasts a server message, and signs
 * out on 401 and 403, but a timeout, a dropped connection or a 504 page has neither.
 */
export function isSilentFailure(error: unknown): boolean {
  const { status, data } = responseOf(error);
  if (status === 401 || status === 403) return false;
  return !(typeof data?.message === 'string' && data.message !== '');
}

/** True when the request got no usable answer (no response, or the gateway gave up): the work may or may not have happened. */
export function isUnansweredRequest(error: unknown): boolean {
  const { status } = responseOf(error);
  return status === undefined || status === 502 || status === 503 || status === 504;
}

/** The reasons of a publish gate refusal (422 PUBLISH_GATE); null for any other error. */
export function gateReasonsOf(error: unknown): PublishGateReason[] | null {
  const { status, data } = responseOf(error);
  if (status !== 422 || data?.code !== 'PUBLISH_GATE' || !Array.isArray(data.reasons)) return null;
  return data.reasons as PublishGateReason[];
}

/** The lines to show when importing a definition failed. */
export function definitionErrorLines(error: unknown): string[] {
  const { status, data } = responseOf(error);
  const message = typeof data?.message === 'string' && data.message ? (data.message as string) : null;

  if (status === 422 && data?.code === 'DEFINITION_DROPS' && Array.isArray(data.reasons)) {
    const reason = (data.reasons as PublishGateReason[]).find(
      (r): r is PrepareDrops => r?.code === 'PREPARE_DROPS',
    );
    if (reason && message) {
      const parts = dropParts(reason);
      const lost = parts.length > 0 ? parts.join(', ') : 'part of this template';
      return [message, `Lost on import: ${lost}. Fix the definition and import it again.`];
    }
  }

  if (data?.errors && typeof data.errors === 'object') {
    const lines = Object.entries(data.errors as Record<string, unknown>)
      .filter(([, messages]) => Array.isArray(messages) && typeof messages[0] === 'string')
      .slice(0, MAX_FIELD_ERRORS)
      .map(([field, messages]) => `${field}: ${(messages as string[])[0]}`);
    if (lines.length > 0) return lines;
  }

  return [message ?? IMPORT_FAILED_TEXT];
}

export type ParsedDefinition = { ok: true; definition: TemplateDefinition } | { ok: false; error: string };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Reads the text of a definition file. Only its shape is checked here; the back end validates the rest. */
export function parseDefinitionJson(text: string): ParsedDefinition {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: "This isn't valid JSON" };
  }
  if (
    !isObject(parsed) ||
    !isObject(parsed.catalogue) ||
    typeof parsed.catalogue.name !== 'string' ||
    parsed.catalogue.name === '' ||
    !isObject(parsed.snapshot)
  ) {
    return { ok: false, error: 'Expected { "catalogue": …, "snapshot": … }, as Export JSON saves it' };
  }
  return { ok: true, definition: parsed as unknown as TemplateDefinition };
}

/** `roofing-quote-request-v2.json`: the template's name as a slug, then its version. */
export function definitionFileName(name: string, versionNo: number): string {
  const slug = name
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'template'}-v${versionNo}.json`;
}

/** The "why it works" lines as the back end keeps them: trimmed, none empty, at most three. */
export function cleanWhyItWorks(lines: string[]): string[] {
  return lines
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .slice(0, 3);
}

export function publishedToastText(versionNo: number, status: TemplateStatus): string {
  if (status === 'published') return `Version ${versionNo} is live`;
  return `Version ${versionNo} saved, the template stays ${STATUS_LABELS[status].toLowerCase()}`;
}

/**
 * The toast after Restore. A template with a version gets "Put back in the library" next, so the toast stays
 * short; one without a version only goes live by publishing one.
 */
export function restoredToastText(hasVersion: boolean): string {
  return hasVersion ? 'Restored as a draft' : 'Restored as a draft, publish a version to put it in the library';
}
